import { cn } from '@nexo/ui';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MIN = 1;
const MAX = 8;
const CLICK_ZOOM = 2.5;

interface View {
  scale: number;
  x: number;
  y: number;
}

const FIT: View = { scale: 1, x: 0, y: 0 };
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * Image fitted to the viewer that zooms towards the pointer: wheel / trackpad pinch, click to
 * toggle, drag to pan, two-finger pinch on touch screens and + / − / 0 on the keyboard.
 */
export function ImageZoom({ src, alt }: { src: string; alt: string }) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const img = useRef<HTMLImageElement>(null);
  const [view, setViewState] = useState<View>(FIT);
  // Gesture handlers read the latest view synchronously between renders.
  const viewRef = useRef<View>(FIT);
  const setView = useCallback((next: View) => {
    viewRef.current = next;
    setViewState(next);
  }, []);
  const [animate, setAnimate] = useState(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef({ moved: false, pinch: null as number | null, startX: 0, startY: 0 });

  /** Keeps the zoomed image covering the viewport instead of drifting off-screen. */
  const bounded = useCallback((next: View): View => {
    const scale = clamp(next.scale, MIN, MAX);
    if (scale === MIN || !box.current || !img.current) return FIT;
    const maxX = Math.max(0, (img.current.offsetWidth * scale - box.current.clientWidth) / 2);
    const maxY = Math.max(0, (img.current.offsetHeight * scale - box.current.clientHeight) / 2);
    return { scale, x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
  }, []);

  /** Zooms to `scale` keeping the image point under (clientX, clientY) in place. */
  const zoomAt = useCallback(
    (scale: number, clientX?: number, clientY?: number) => {
      const rect = box.current?.getBoundingClientRect();
      if (!rect) return;
      const current = viewRef.current;
      const target = clamp(scale, MIN, MAX);
      const px = (clientX ?? rect.left + rect.width / 2) - (rect.left + rect.width / 2);
      const py = (clientY ?? rect.top + rect.height / 2) - (rect.top + rect.height / 2);
      const ratio = target / current.scale;
      setView(
        bounded({
          scale: target,
          x: px - (px - current.x) * ratio,
          y: py - (py - current.y) * ratio,
        }),
      );
    },
    [bounded, setView],
  );

  const step = useCallback(
    (factor: number) => {
      setAnimate(true);
      zoomAt(viewRef.current.scale * factor);
    },
    [zoomAt],
  );

  const reset = useCallback(() => {
    setAnimate(true);
    setView(FIT);
  }, [setView]);

  // Wheel needs a non-passive listener to stop the page from scrolling.
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      setAnimate(false);
      // Trackpad pinch arrives as ctrl+wheel with small deltas.
      const speed = event.ctrlKey ? 0.01 : 0.002;
      zoomAt(viewRef.current.scale * Math.exp(-event.deltaY * speed), event.clientX, event.clientY);
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === '+' || event.key === '=') step(1.5);
      else if (event.key === '-') step(1 / 1.5);
      else if (event.key === '0') reset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, reset]);

  // Refit when the window size changes the fitted image size.
  useEffect(() => {
    const onResize = () => setView(bounded(viewRef.current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [bounded, setView]);

  const distance = () => {
    const [a, b] = [...pointers.current.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 1) {
      gesture.current = { moved: false, pinch: null, startX: event.clientX, startY: event.clientY };
    }
    if (pointers.current.size === 2)
      gesture.current = { ...gesture.current, moved: true, pinch: distance() };
    setAnimate(false);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const dx = event.clientX - previous.x;
    const dy = event.clientY - previous.y;

    if (pointers.current.size === 2 && gesture.current.pinch) {
      const now = distance();
      const [a, b] = [...pointers.current.values()];
      const ratio = now / gesture.current.pinch;
      gesture.current.pinch = now;
      zoomAt(
        viewRef.current.scale * ratio,
        a && b ? (a.x + b.x) / 2 : undefined,
        a && b ? (a.y + b.y) / 2 : undefined,
      );
      return;
    }
    const { startX, startY } = gesture.current;
    if (Math.hypot(event.clientX - startX, event.clientY - startY) > 4)
      gesture.current.moved = true;
    const current = viewRef.current;
    if (current.scale > MIN) setView(bounded({ ...current, x: current.x + dx, y: current.y + dy }));
  };

  const onPointerUp = (event: React.PointerEvent) => {
    const wasClick = pointers.current.size === 1 && !gesture.current.moved;
    pointers.current.delete(event.pointerId);
    if (!wasClick || event.type === 'pointercancel') return;
    setAnimate(true);
    if (viewRef.current.scale > MIN) reset();
    else zoomAt(CLICK_ZOOM, event.clientX, event.clientY);
  };

  const zoomed = view.scale > MIN;

  return (
    <div className="relative h-full w-full">
      <div
        ref={box}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={cn(
          'flex h-full w-full touch-none items-center justify-center overflow-hidden select-none',
          zoomed ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in',
        )}
      >
        <img
          ref={img}
          src={src}
          alt={alt}
          draggable={false}
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}
          className={cn(
            'max-h-full max-w-full rounded-lg object-contain shadow-2xl will-change-transform',
            animate && 'transition-transform duration-200 ease-out',
          )}
        />
      </div>
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-lg bg-black/60 p-1 text-xs text-white/90 backdrop-blur">
        <ZoomButton label={t('Alejar (−)')} onClick={() => step(1 / 1.5)} disabled={!zoomed}>
          <Minus />
        </ZoomButton>
        <button
          type="button"
          onClick={reset}
          title={t('Ajustar a la pantalla (0)')}
          className="min-w-14 rounded-md px-2 py-1 tabular-nums hover:bg-white/15"
        >
          {Math.round(view.scale * 100)}%
        </button>
        <ZoomButton label={t('Acercar (+)')} onClick={() => step(1.5)} disabled={view.scale >= MAX}>
          <Plus />
        </ZoomButton>
        {zoomed && (
          <ZoomButton label={t('Ajustar (0)')} onClick={reset}>
            <RotateCcw />
          </ZoomButton>
        )}
      </div>
    </div>
  );
}

function ZoomButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex size-7 items-center justify-center rounded-md hover:bg-white/15 disabled:opacity-40 disabled:hover:bg-transparent [&_svg]:size-4"
    >
      {children}
    </button>
  );
}
